import { createSlice } from "@reduxjs/toolkit"
import { remove, filter } from "lodash"
import { batch } from "react-redux"
import { notification } from "antd"

// http client
import client from "../../../lib/client"
import {
    accountingBatchesUrl,
    accountingBatchUrl,
    accountingBatchesSidebar,
    dynamicUrl,
    batchGroupSelectOptionsUrl,
    accountingBatchBillsUrl,
    accountingBatchNewBillsUrl,
    accountingBatchBillsCsvUrl,
    accountingBatchCsvUrl,
    accountingBatchGenerateUploadFileUrl,
    accountingBatchGenerateInvoicePdfUrl,
    accountingBatchGenerateBillImagesZipUrl,
    bulkEditAccountingBatchesUrl,
} from "../../../lib/endpoints"
import { createFilterUrl } from "../../../functions/objectFunctions/createFilterUrl"

export const accountingBatchesSlice = createSlice({
    name: "accountingBatches",
    initialState: {
        accountingBatch: {},
        accountingBatches: [],
        accountingBatchSelectOptions: [],
        accountingBatchSearch: [],
        batchGroupSelectOptions: [],
        total: 0,
        pagination: { current: 1, pageSize: 10 },
        bills: [],
        fetchingBills: false,
        loading: false,
        filters: {},
        bulkEditBatchIds: [],
        sort: {
            column: "batch_date",
            direction: "desc",
            secondaryColumn: "created_at",
            secondaryDirection: "desc",
        },
    },
    reducers: {
        setAccountingBatch: (state, action) => {
            state.accountingBatch = action.payload
        },
        setAccountingBatches: (state, action) => {
            state.accountingBatches = action.payload.accounting_batches
            state.total = action.payload.total
        },
        setPagination: (state, action) => {
            state.pagination = action.payload
        },
        setLoading: (state, action) => {
            state.loading = action.payload
        },
        setAccountingBatchSelectOptions: (state, action) => {
            state.accountingBatchSelectOptions = action.payload
        },
        setAccountingBatchSearch: (state, action) => {
            state.accountingBatchSearch = action.payload
        },
        setBatchGroupSelectOptions: (state, action) => {
            state.batchGroupSelectOptions = action.payload
        },
        setBills: (state, action) => {
            state.bills = action.payload
        },
        setFilters: (state, action) => {
            state.filters = action.payload
        },
        setSort: (state, action) => {
            state.sort = action.payload
        },
        deleteAccountingBatch: (state, action) => {
            state.accountingBatches = state.accountingBatches.filter((accountingBatch) => accountingBatch.id !== action.payload)
            state.total = state.total - 1
        },
        setBulkEditBatchIds: (state, action) => {
            state.bulkEditBatchIds = action.payload
        },
        disableGenerateUploadFile: (state, action) => {
            try {
                state.accountingBatches = state.accountingBatches.map((ab) => {
                    if (ab.id === action.payload) {
                        return {
                            ...ab,
                            can_generate_upload_file: false,
                            generating_file: true,
                        }
                    } else {
                        return ab
                    }
                })
            } catch (e) {
                console.error("Error disabling upload file generation: ", e)
                return state
            }
        },
        updateUploadFile: (state, action) => {
            try {
                state.accountingBatches = state.accountingBatches.map((ab) => {
                    if (ab.id === action.payload.accounting_batch_id) {
                        return {
                            ...ab,
                            upload_file_url: action.payload.link,
                            generating_file: false,
                            // The job says whether the data moved on while it was building.
                            stale_files: { ...(ab.stale_files || {}), upload_file: !!action.payload.stale },
                        }
                    } else {
                        return ab
                    }
                })
            } catch (e) {
                console.error("Upload file generation error: ", e)
                return state
            }
        },
        // The invoice PDF is now stored on the batch, so the table can show it like the upload file.
        startInvoicePdf: (state, action) => {
            state.accountingBatches = state.accountingBatches.map((ab) =>
                ab.id === action.payload ? { ...ab, generating_invoice_pdf: true } : ab
            )
        },
        // From the job's cable broadcast, or from a failed request. On error only the spinner is
        // cleared -- whatever PDF the batch already had is still valid.
        updateInvoicePdf: (state, action) => {
            const { accounting_batch_id: id, error, link, generated_at } = action.payload
            state.accountingBatches = state.accountingBatches.map((ab) => {
                if (ab.id !== id) return ab
                if (error) return { ...ab, generating_invoice_pdf: false }
                return {
                    ...ab,
                    generating_invoice_pdf: false,
                    invoice_pdf_url: link,
                    invoice_pdf_generated_at: generated_at,
                    stale_files: { ...(ab.stale_files || {}), invoice_pdf: !!action.payload.stale },
                }
            })
        },
        startBillImagesZip: (state, action) => {
            state.accountingBatches = state.accountingBatches.map((ab) =>
                ab.id === action.payload ? { ...ab, generating_bill_images_zip: true } : ab
            )
        },
        // Same as updateInvoicePdf. The zip broadcasts `zip_url` rather than `link`, so it stays out
        // of the bell -- a finalize would otherwise add one bell entry per batch.
        updateBillImagesZip: (state, action) => {
            const { accounting_batch_id: id, error, zip_url: zipUrl } = action.payload
            state.accountingBatches = state.accountingBatches.map((ab) => {
                if (ab.id !== id) return ab
                if (error) return { ...ab, generating_bill_images_zip: false }
                return {
                    ...ab,
                    generating_bill_images_zip: false,
                    bill_images_zip_url: zipUrl,
                    stale_files: { ...(ab.stale_files || {}), bill_images_zip: !!action.payload.stale },
                }
            })
        },
    },
})

export const {
    setAccountingBatch,
    setAccountingBatches,
    setPagination,
    setLoading,
    setAccountingBatchSelectOptions,
    setAccountingBatchSearch,
    setBatchGroupSelectOptions,
    setBills,
    disableGenerateUploadFile,
    updateUploadFile,
    startInvoicePdf,
    updateInvoicePdf,
    startBillImagesZip,
    updateBillImagesZip,
    setFilters,
    setSort,
    setBulkEditBatchIds
} = accountingBatchesSlice.actions

export function fetchAccountingBatch(id) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        client.get(accountingBatchUrl(id)).then((response) => {
            dispatch(setAccountingBatch(response.data))
            dispatch(setLoading(false))
        })
    }
}

export function fetchAccountingBatches(clientId, pagination) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        // console.log(client)
        if (pagination?.values) {
            const response = await client.get(`${accountingBatchesUrl(clientId)}?values=[${pagination.values}]`)
            return response.data
        }

        client.get(`${accountingBatchesUrl(clientId)}?page=${pagination.current}&per=${pagination.pageSize}`).then((response) => {
            dispatch(setPagination(pagination))
            dispatch(setAccountingBatches(response.data))
            dispatch(setLoading(false))
        })
    }
}

export function filterAccountingBatches(query) {
    /// create the filter URL string
    let filterUrl = createFilterUrl(query, accountingBatchesUrl(query.clientId))

    return async (dispatch) => {
        dispatch(setLoading(true))
        client
            .get(filterUrl)
            .then((response) => {
                dispatch(setPagination(query.pagination))
                dispatch(setAccountingBatches(response.data))
                dispatch(setLoading(false))
            })
            .catch((error) => {
                console.warn(error)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function fetchAccountingBatchesSidebar(clientId) {
    return async (dispatch, getState) => {
        const response = await client.get(`${accountingBatchesSidebar(clientId)}`)
        return response.data.accounting_batches
    }
}

export function submitAccountingBatch(values, id, clientId, formatDate = true) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        if (formatDate) {
            if (id) {
                // patch - update
                const response = await client.patch(accountingBatchUrl(id, clientId), { accounting_batch: { ...values, batch_date: new Date(values.batch_date).toISOString() } })

                if (!!response.data.bills) {
                    dispatch(setBills(response.data.bills))
                }
                dispatch(setLoading(false))
                return response.data
            } else {
                // post - create
                const response = await client.post(accountingBatchesUrl(clientId), { accounting_batch: { ...values, batch_date: new Date(values.batch_date).toISOString() } })
                dispatch(setLoading(false))
                return response.data
            }
        } else {
            if (id) {
                // patch - update
                const response = await client.patch(accountingBatchUrl(id, clientId), { accounting_batch: values })

                if (!!response.data.bills) {
                    dispatch(setBills(response.data.bills))
                }
                dispatch(setLoading(false))
                return response.data
            } else {
                // post - create
                const response = await client.post(accountingBatchesUrl(clientId), { accounting_batch: values })
                dispatch(setLoading(false))
                return response.data
            }
        }
    }
}

export function deleteAccountingBatch(id) {
    return async (dispatch, getState) => {
        client
            .delete(`${accountingBatchUrl(id)}`)
            .then(({ data }) => {
                if (data.success) {
                    notification.success({
                        message: data.message,
                        duration: 0.8,
                    })

                    dispatch(accountingBatchesSlice.actions.deleteAccountingBatch(id))
                } else {
                    notification.error({
                        message: data.message,
                        duration: 0.8,
                    })
                }
            })
            .catch((e) => {
                notification.error({
                    message: "Request error",
                    description: e?.response?.statusText,
                    duration: 0.8,
                })
                console.error(e)
            })
    }
}

export function fetchBatchGroupSelectOptions(clientId) {
    return async (dispatch, getState) => {
        client.get(`${batchGroupSelectOptionsUrl(clientId)}`).then((response) => {
            dispatch(setBatchGroupSelectOptions(response.data.batch_groups))
        })
    }
}

export function fetchAccountingBatchBills(clientId, accountingBatchId) {
    return async (dispatch, getState) => {
        client.get(`${accountingBatchBillsUrl(clientId, accountingBatchId)}`).then((response) => {
            dispatch(setBills(response.data.bills))
        })
    }
}

export function fetchAccountingBatchNewBills(clientId, accountingBatchId) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        const response = await client.get(`${accountingBatchNewBillsUrl(clientId, accountingBatchId)}`)
        dispatch(setBills(response.data.bills))
        dispatch(setLoading(false))
        return { success: true, new_bills: response.data.new_bills, total: response.data.new_bills_total }
    }
}

// export function deleteAccountingBatch(accountingBatchId) {
//     return async (dispatch, getState) => {
//         const response = await client.delete(`${accountingBatchUrl(accountingBatchId)}`)
//         if (response.data.success) {
//             let batches = [...getState().accountingBatches.accountingBatches]

//             let newList = filter(batches, function (n) {
//                 return n.id !== parseInt(accountingBatchId)
//             })

//             dispatch(setAccountingBatches({ accounting_batches: [...newList], total: getState().accountingBatches.total - 1 }))
//             return response.data
//         } else {
//             return response.data
//         }
//     }
// }

export function generateCsv(accountingBatchId, clientId) {
    return async (dispatch, getState) => {
        const url = accountingBatchBillsCsvUrl(accountingBatchId, clientId)
        const separator = url.includes("?") ? "&" : "?"
        client.get(`${url}${separator}connection_id=${getState().app.uid}`)
    }
}

export function generateAccountingBatchCsv(clientId) {
    return async (dispatch, getState) => {
        client.get(`${accountingBatchCsvUrl(clientId)}?connection_id=${getState().app.uid}`)
    }
}

export function generateAccountingBatchUploadFile(accountingBatchId, clientId) {
    return async (dispatch, getState) => {
        dispatch(disableGenerateUploadFile(accountingBatchId))
        const url = accountingBatchGenerateUploadFileUrl(accountingBatchId, clientId)
        const separator = url.includes("?") ? "&" : "?"
        client.get(`${url}${separator}connection_id=${getState().app.uid}`)
    }
}

export function generateAccountingBatchInvoicePdf(accountingBatchId, clientId) {
    return async (dispatch, getState) => {
        dispatch(startInvoicePdf(accountingBatchId))
        const url = accountingBatchGenerateInvoicePdfUrl(accountingBatchId, clientId)
        const separator = url.includes("?") ? "&" : "?"
        // If the request itself fails, no broadcast will ever arrive -- clear the spinner here.
        client
            .get(`${url}${separator}connection_id=${getState().app.uid}`)
            .catch(() => dispatch(updateInvoicePdf({ accounting_batch_id: accountingBatchId, error: true })))
    }
}

export function generateAccountingBatchBillImagesZip(accountingBatchId, clientId) {
    return async (dispatch, getState) => {
        dispatch(startBillImagesZip(accountingBatchId))
        const url = accountingBatchGenerateBillImagesZipUrl(accountingBatchId, clientId)
        const separator = url.includes("?") ? "&" : "?"
        // If the request itself fails, no broadcast will ever arrive -- clear the spinner here.
        client
            .get(`${url}${separator}connection_id=${getState().app.uid}`)
            .catch(() => dispatch(updateBillImagesZip({ accounting_batch_id: accountingBatchId, error: true })))
    }
}

// export function fetchAccountingBatchSelectOptions(clientId) {
//     return async (dispatch, getState) => {
//         client.get(`${accountingBatchSelectOptionsUrl(clientId)}`).then((response) => {
//             dispatch(setAccountingBatchSelectOptions(response.data.accounting_batches))
//         })
//     }
// }

// export function fetchAccountingBatchSelectOptionById(id, currentPath) {
//     return async (dispatch, getState) => {
//         client.get(`${accountingBatchSelectOptionByIdUrl}?accountingBatch_id=${id}`).then((response) => {
//             dispatch(setAccountingBatchSelectOptions(response.data.accountingBatch))
//         })
//     }
// }

export function searchAccountingBatches(query, url) {
    return async (dispatch) => {
        dispatch(setLoading(true))

        client
            .get(`${dynamicUrl(url)}?q=${query}`)
            .then(({ data }) => {
                dispatch(setAccountingBatchSearch(data))
            })
            .catch((error) => {
                console.warn(error)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function editBatches(info, query){
    let filterUrl = createFilterUrl(query, accountingBatchesUrl(query.clientId))

    return async (dispatch) => {
        dispatch(setLoading(true))
        client
            .post(`${bulkEditAccountingBatchesUrl(query.clientId)}`, { accounting_batch_info: info })
            .catch((e) => {
                dispatch(setLoading(false))
                console.log("ERROR", e)
            })
            .finally(() => {
                dispatch(setBulkEditBatchIds([]))
                client
                    .get(filterUrl)
                    .then((response) => {
                        dispatch(setPagination(query.pagination))
                        dispatch(setAccountingBatches(response.data))
                        dispatch(setLoading(false))
                    })
                    .catch((error) => {
                        console.warn(error)
                    })
                    .finally(() => {
                        dispatch(setLoading(false))
                    })
            })
    }
}

export const selectAccountingBatch = (state) => state.accountingBatches.accountingBatch
export const selectAccountingBatches = (state) => state.accountingBatches.accountingBatches
export const selectTotal = (state) => state.accountingBatches.total
export const selectPagination = (state) => state.accountingBatches.pagination
export const selectLoading = (state) => state.accountingBatches.loading
export const selectAccountingBatchSelectOptions = (state) => state.accountingBatches.accountingBatchSelectOptions
export const selectAccountingBatchSearch = (state) => state.accountingBatches.accountingBatchSearch
export const selectBatchGroupSelectOptions = (state) => state.accountingBatches.batchGroupSelectOptions
export const selectBills = (state) => state.accountingBatches.bills
export const selectFilters = (state) => state.accountingBatches.filters
export const selectSort = (state) => state.accountingBatches.sort
export const selectBulkEditBatchIds = (state) => state.accountingBatches.bulkEditBatchIds

export default accountingBatchesSlice.reducer
